import { useEffect, useState } from "react";

/**
 * useBreakpoint — simple hook that reports the current breakpoint.
 *
 * Tailwind-style thresholds:
 *   mobile  : < 768px
 *   tablet  : 768px – 1279px
 *   desktop : >= 1280px
 *
 * Returns an object `{ bp, isMobile, isTablet, isDesktop }` so consumers can
 * branch cheaply without re-computing. SSR-safe (returns "desktop" as a
 * sensible default on the server) and tolerant of missing matchMedia.
 */
export function useBreakpoint() {
  const get = () => {
    if (typeof window === "undefined") return "desktop";
    const w = window.innerWidth;
    if (w >= 1280) return "desktop";
    if (w >= 768) return "tablet";
    return "mobile";
  };

  const [bp, setBp] = useState(get);

  useEffect(() => {
    const onResize = () => setBp(get());
    window.addEventListener("resize", onResize);
    window.addEventListener("orientationchange", onResize);
    // Fire once to sync (covers hydration mismatches)
    onResize();
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("orientationchange", onResize);
    };
  }, []);

  return {
    bp,
    isMobile: bp === "mobile",
    isTablet: bp === "tablet",
    isDesktop: bp === "desktop",
  };
}
